import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, BuildingData, DataWithUpload, Draft, useBuildingMutation, useStore } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { BuildingForm, BuildingHeader, LayoutHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const BuildingAdd = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const store = useStore()
  const { message } = store
  const [{ companyId, locationId }] = useNumberParams()
  const mutation = useBuildingMutation({ companyId })
  const router = useRouter()
  const { t } = useTranslation()

  const add = async (draft: Draft<DataWithUpload<BuildingData>>) => {
    setErrors({})
    const { src: _src, upload, ...otherData } = draft.data
    try {
      const building = await mutation.create(new Draft(otherData))
      draft.update('id', building.id)
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`BUILDING: Building failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Building could not be created')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }
    draft.settle()
    message.add({
      message: t('Building was created successfully'),
      status: 'success',
    })

    if (upload) {
      try {
        await mutation.upload(draft, upload)
      } catch (error) {
        const { message: errorMessage } = error as Error
        console.debug(`BUILDING: Building upload failed: ${errorMessage}`)
        message.add({
          message: `${t('Building image could not be uploaded')}. ${errorMessage}`,
          status: 'error',
        })
        draft.settle()
        router.push(`/companies/${companyId}/locations/${locationId}`)
        return
      }
    }
    router.push(`/companies/${companyId}/locations/${locationId}`)
  }

  const uploadError = (errors?: Record<string, string>) => {
    setErrors(errors)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <BuildingHeader
          companyId={companyId}
          locationId={locationId}
          title={t('Add building')}
          breadcrumbItems={[{ title: t('Add building') }]}
        />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<BuildingForm.Skeleton numberOfbuttons={2} />}>
          <BuildingForm
            companyId={companyId}
            locationId={locationId}
            onSave={add}
            onError={uploadError}
            cancelHref={`/companies/${companyId}/locations/${locationId}`}
            errors={errors}
          />
        </Suspense>
      </View>
    </View>
  )
}

export default BuildingAdd
